import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Grid, MenuItem, IconButton, TextField, Stack } from "~/common/mui";
import DateInput from "~/common/components/DateInput";
import Delete from "~/common/mui/icons/Delete";
import { isDateInvalidForDelivery } from "~/common/utils/dateUtils";
import type { ProcessesedRows } from "../types/models";
import { type QuotePreviewFormValues } from "./QuoteSplitDialog";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";

export enum AlterQuoteQuantity {
  ALLOW_ALL = "ALLOW_ALL",
  DENY_ALL = "DENY_ALL",
  ALLOW_EXCEED_ONLY = "ALLOW_EXCEED_ONLY",
}

interface QuoteProps {
  actualQuoteIndex: number;
  index: number;
  lineItemInfo: ProcessesedRows;
  quotedQtyForLineItem: number;
  positionOptions: number[];
  materialNumberOptions: (string | undefined)[];
  getTotalQtyOfRepeatedMaterials: (position: number, materialNumber: string, updatedValue: number) => number;
  checkAndUpdatePosition: (index: number, update: ProcessesedRows) => void;
  removeItem: (index: number) => void;
}

export const QuotePreviewLineItem = ({
  index,
  actualQuoteIndex,
  lineItemInfo,
  quotedQtyForLineItem,
  positionOptions,
  materialNumberOptions,
  getTotalQtyOfRepeatedMaterials,
  checkAndUpdatePosition,
  removeItem,
}: QuoteProps) => {
  const { t } = useTranslation();
  const { lineItemId, position, product, quantity } = lineItemInfo;

  const { control, trigger, setValue, getValues } = useFormContext();

  const [watchPosition, watchQuantity, watchMaterialId] = useWatch({
    name: [
      `rows.${actualQuoteIndex}.items.${index}.position`,
      `rows.${actualQuoteIndex}.items.${index}.quantity`,
      `rows.${actualQuoteIndex}.items.${index}.product`,
    ],
  });

  const triggerAllQuantities = useCallback(async () => {
    const items = getValues(`rows.${actualQuoteIndex}.items`) ?? [];

    const fields = items.map((_: QuotePreviewFormValues, i: number) => `rows.${actualQuoteIndex}.items.${i}.quantity`);
    await trigger(fields);
  }, [actualQuoteIndex, getValues, trigger]);

  useEffect(() => {
    // Trigger all quantities validation manually to enforce cross-field rules when position or quantity changes.
    if (watchPosition || watchQuantity || watchMaterialId) {
      triggerAllQuantities();
    }
  }, [watchPosition, watchQuantity, watchMaterialId, triggerAllQuantities]);

  const alterQuoteQuantityPermission = window.SCHA.alterQuoteQuantity as AlterQuoteQuantity;

  const isInValid = (value: number) => {
    const total = getTotalQtyOfRepeatedMaterials(position, product, lineItemId) + value;

    switch (alterQuoteQuantityPermission) {
      case AlterQuoteQuantity.DENY_ALL:
        return total !== quotedQtyForLineItem || value == 0;

      case AlterQuoteQuantity.ALLOW_EXCEED_ONLY:
        return total < quotedQtyForLineItem || value == 0;

      default:
        return value == 0;
    }
  };

  return (
    <Stack direction={"row"} gap={3} alignItems={"flex-start"}>
      <Grid container size={12} spacing={3} flexGrow={1}>
        <Grid size={1.5}>
          <Controller
            name={`rows.${actualQuoteIndex}.items.${index}.position`}
            control={control}
            rules={{
              validate: (value) => value > 0 || t("quotation.split.preview.errorMessage.position"),
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                select
                size="small"
                label={t("quotation.split.preview.position")}
                value={value}
                onChange={(e) => {
                  onChange(Number(e.target.value));
                }}
                error={!!error}
                helperText={error?.message}
                disabled={positionOptions.length <= 1}
              >
                {value === 0 && (
                  <MenuItem value={0} disabled>
                    -
                  </MenuItem>
                )}
                {(positionOptions.length > 0 ? positionOptions : [value]).map((pos) => (
                  <MenuItem key={pos} value={pos}>
                    {pos}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid size={3.5}>
          <Controller
            name={`rows.${actualQuoteIndex}.items.${index}.product`}
            control={control}
            render={({ field: { value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                select
                size="small"
                label={t("quotation.split.preview.materialNumber")}
                value={value}
                onChange={(e) => {
                  const newValue = e.target.value as string;
                  checkAndUpdatePosition(index, { ...lineItemInfo, product: newValue });
                }}
                error={!!error}
                helperText={error?.message}
              >
                {materialNumberOptions.map((i) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid size={3.5}>
          <Controller
            name={`rows.${actualQuoteIndex}.items.${index}.quantity`}
            control={control}
            rules={{
              validate: {
                quantityMatch: (value) => {
                  return isInValid(value)
                    ? t("quotation.split.preview.errorMessage.quantity", {
                        quoteQuantity: NF.getDefaultFormatter().format(quotedQtyForLineItem),
                      })
                    : true;
                },
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <QuantityInput
                step={1}
                hideButtons={true}
                min={1}
                fullWidth
                size="small"
                label={t("input.quantity")}
                value={value}
                onValueChange={(value) => {
                  onChange(value);
                }}
                showErrorIcon={true}
                roundToStep={false}
                error={!!error}
                helperLabel={
                  error?.message
                    ? error.message
                    : t(`quotation.split.preview.helperText.quantity`, {
                        quoteQuantity: NF.getDefaultFormatter().format(quotedQtyForLineItem),
                      })
                }
              />
            )}
          />
        </Grid>

        <Grid size={3.5}>
          <Controller
            name={`rows.${actualQuoteIndex}.items.${index}.date`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DateInput
                label={t("quotation.split.preview.rdd")}
                date={value}
                onChange={(newDate) => {
                  onChange(newDate);
                }}
                size={"small"}
                isInvalid={!!error}
                isDayDisabled={isDateInvalidForDelivery}
              />
            )}
          />
        </Grid>
      </Grid>
      <IconButton
        color="textSecondary"
        onClick={() => {
          removeItem(index);
        }}
      >
        <Delete />
      </IconButton>
    </Stack>
  );
};
